import { useMemo, type CSSProperties } from "react";
import { useParallax } from "../hooks/useParallax";
import { useMousePosition } from "../hooks/useMousePosition";
import { heroMetrics } from "../data/heroMetrics";

type ResearchModule = {
  id: string;
  depth: "far" | "mid" | "near";
  top: string;
  left: string;
  width: string;
  height: string;
  blur: number;
  opacity: number;
  content: "formula" | "nav-chart" | "tdd-label" | "annotated" | "graph" | "micro-grid" | "metrics" | "trading-chart" | "nav-compound-chart";
  docCode?: string;
  annotations?: string[];
};

const researchModules: ResearchModule[] = [
  // CARD 1: BACK CARD (far depth) - Swapped with CARD 6 - METRICS
  {
    id: "module-far-1",
    depth: "far",
    top: "55%",
    left: "68%",
    width: "360px",
    height: "200px",
    blur: 22,
    opacity: 0.14,
    content: "metrics",
    docCode: "SYSTEM METRICS",
    annotations: [],
  },
  // CARD 2: BACK CARD (far depth) - Swapped with CARD 3 - NAV Chart
  {
    id: "module-far-2",
    depth: "far",
    top: "30%",
    left: "26%",
    width: "340px",
    height: "200px",
    blur: 22,
    opacity: 0.14,
    content: "nav-chart",
    annotations: ["NAV TRACKING", "DOC 01 • REV 0.4"],
  },
  // CARD 3: MID CARD (mid depth) - Swapped with CARD 2 - TRADING CHART
  {
    id: "module-mid-1",
    depth: "mid",
    top: "64%",
    left: "78%",
    width: "280px",
    height: "160px",
    blur: 12,
    opacity: 0.18,
    content: "trading-chart",
    docCode: "PRICE CHART",
    annotations: [],
  },
  // CARD 4: MID CARD (mid depth) - Graph
  {
    id: "module-mid-2",
    depth: "mid",
    top: "68%",
    left: "10%",
    width: "320px",
    height: "190px",
    blur: 12,
    opacity: 0.18,
    content: "graph",
    annotations: ["ARB FLOW", "VALIDATION: 2025-11-04"],
  },
  // CARD 5: FRONT CARD (near depth) - Center - Metrics
  {
    id: "module-near-1",
    depth: "near",
    top: "35%",
    left: "62%",
    width: "400px",
    height: "280px",
    blur: 6,
    opacity: 0.18,
    content: "metrics",
    docCode: "SYSTEM METRICS",
    annotations: [],
  },
  // CARD 6: FRONT CARD (near depth) - Swapped with CARD 1 - TRADING CHART
  {
    id: "module-near-2",
    depth: "near",
    top: "18%",
    left: "6%",
    width: "300px",
    height: "180px",
    blur: 4,
    opacity: 0.22,
    content: "trading-chart",
    docCode: "PRICE CHART",
    annotations: [],
  },
  // CARD 7: FRONT CARD (near depth) - Top left - NAV Comparison Chart
  {
    id: "module-near-3",
    depth: "near",
    top: "10%",
    left: "84%",
    width: "320px",
    height: "200px",
    blur: 6,
    opacity: 0.22,
    content: "nav-compound-chart",
    docCode: "NAV TRACKING",
    annotations: ["NAV vs AUTO-COMPOUND"],
  },
];

interface HeroLayersProps {
  isMobile: boolean;
}

export function HeroLayers({ isMobile }: HeroLayersProps) {
  // Parallax speeds: back=1px/scroll, mid=2px/scroll, front=4px/scroll
  // Vertical parallax for up/down movement: negative values move up on scroll
  const parallaxFarX = useParallax(isMobile ? 0 : 0.001);
  const parallaxMidX = useParallax(isMobile ? 0 : 0.002);
  const parallaxNearX = useParallax(isMobile ? 0 : 0.004);
  
  // Vertical parallax for cards moving up/down on scroll
  const parallaxFarY = useParallax(isMobile ? 0 : -0.0005);
  const parallaxMidY = useParallax(isMobile ? 0 : -0.001);
  const parallaxNearY = useParallax(isMobile ? 0 : -0.0015);

  // Mouse position for hover drift (desktop only)
  const mousePos = useMousePosition();
  const hoverOffset = useMemo(() => {
    if (isMobile || typeof window === "undefined") {
      return { x: 0, y: 0 };
    }

    // Calculate offset from center of viewport
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const maxOffsetX = 8;
    const maxOffsetY = 6;

    const offsetX = ((mousePos.x - centerX) / centerX) * maxOffsetX;
    const offsetY = ((mousePos.y - centerY) / centerY) * maxOffsetY;

    return { x: offsetX, y: offsetY };
  }, [mousePos.x, mousePos.y, isMobile]);

  const getParallaxOffset = (depth: "far" | "mid" | "near") => {
    switch (depth) {
      case "far":
        return { x: parallaxFarX, y: parallaxFarY };
      case "mid":
        return { x: parallaxMidX, y: parallaxMidY };
      case "near":
        return { x: parallaxNearX, y: parallaxNearY };
    }
  };

  return (
    <div className="absolute inset-0 pointer-events-none w-full max-w-full overflow-hidden" aria-hidden="true">
      {researchModules.map((module) => {
        const parallax = getParallaxOffset(module.depth);
        const driftX = hoverOffset.x * (module.depth === "near" ? 1 : module.depth === "mid" ? 0.6 : 0.3);
        const driftY = hoverOffset.y * (module.depth === "near" ? 1 : module.depth === "mid" ? 0.6 : 0.3);

        // Mobile: single layer display, reduced blur
        const mobileBlur = isMobile ? Math.min(module.blur, 2) : module.blur;
        const finalParallaxX = isMobile ? 0 : parallax.x + driftX;
        const finalParallaxY = isMobile ? 0 : parallax.y + driftY;

        return (
          <div
            key={module.id}
            className="research-module"
            style={
              {
                top: module.top,
                left: module.left,
                width: module.width,
                height: module.height,
                opacity: module.opacity,
                filter: `blur(${mobileBlur}px)`,
                transform: `translate3d(${finalParallaxX}px, ${finalParallaxY}px, 0)`,
                transition: isMobile ? "none" : "transform 0.1s ease-out",
                display: isMobile && module.depth !== "near" ? "none" : "block",
              } as CSSProperties
            }
          >
            <div className="research-module-inner">
              {/* Micro-grid background */}
              <div className="absolute inset-0 bg-grid-faint bg-grid-sm opacity-[0.015]" />

              {module.content === "trading-chart" && (
                <div className="relative h-full w-full p-4">
                  {/* Trading chart with line graph and volume bars */}
                  <div className="relative h-full w-full">
                    {module.docCode && (
                      <div className="absolute top-2 left-4 font-mono text-[8px] uppercase tracking-[0.15em] text-thema-faint opacity-40 z-10">
                        {module.docCode}
                      </div>
                    )}
                    
                    {/* Chart container */}
                    <div className="absolute inset-0 rounded border border-thema-border-hairline bg-thema-surface/10 overflow-hidden">
                      <svg className="h-full w-full" viewBox="0 0 400 200" preserveAspectRatio="none">
                        <defs>
                          {/* Grid lines */}
                          <pattern id="chartGrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                            <line x1="0" y1="40" x2="40" y2="40" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
                            <line x1="40" y1="0" x2="40" y2="40" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
                          </pattern>
                          
                          {/* Gradient for line */}
                          <linearGradient id="chartLineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgba(77,183,247,0.6)" />
                            <stop offset="50%" stopColor="rgba(77,183,247,0.3)" />
                            <stop offset="100%" stopColor="rgba(77,183,247,0.1)" />
                          </linearGradient>
                          
                          {/* Gradient for area fill */}
                          <linearGradient id="chartAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgba(16,185,129,0.2)" />
                            <stop offset="50%" stopColor="rgba(16,185,129,0.1)" />
                            <stop offset="100%" stopColor="rgba(16,185,129,0.05)" />
                          </linearGradient>
                          
                          {/* Red gradient for negative area */}
                          <linearGradient id="chartAreaRedGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgba(248,113,113,0.2)" />
                            <stop offset="50%" stopColor="rgba(248,113,113,0.1)" />
                            <stop offset="100%" stopColor="rgba(248,113,113,0.05)" />
                          </linearGradient>
                        </defs>
                        
                        {/* Background grid */}
                        <rect width="400" height="200" fill="url(#chartGrid)" />
                        
                        {/* Reference line (101.24K level) */}
                        <line 
                          x1="0" 
                          y1="100" 
                          x2="400" 
                          y2="100" 
                          stroke="rgba(255,255,255,0.15)" 
                          strokeWidth="1" 
                          strokeDasharray="4,4"
                        />
                        <text x="10" y="95" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace">101.24K</text>
                        
                        {/* Trading line - starts high, drops, then rises, then drops again */}
                        <path
                          d="M 0,40 
                             L 30,80 
                             L 60,120 
                             L 90,110 
                             L 120,140 
                             L 150,100 
                             L 180,60 
                             L 210,50 
                             L 240,45 
                             L 270,55 
                             L 300,70 
                             L 330,85 
                             L 360,95 
                             L 390,105 
                             L 400,110"
                          fill="none"
                          stroke="url(#chartLineGradient)"
                          strokeWidth="2"
                        />
                        
                        {/* Green area fill (above reference line) */}
                        <path
                          d="M 0,100 
                             L 150,100 
                             L 150,60 
                             L 180,50 
                             L 210,45 
                             L 240,50 
                             L 270,55 
                             L 300,70 
                             L 330,85 
                             L 360,95 
                             L 390,105 
                             L 400,110 
                             L 400,100 
                             Z"
                          fill="url(#chartAreaGradient)"
                        />
                        
                        {/* Red area fill (below reference line) */}
                        <path
                          d="M 0,100 
                             L 0,40 
                             L 30,80 
                             L 60,120 
                             L 90,110 
                             L 120,140 
                             L 150,100 
                             Z"
                          fill="url(#chartAreaRedGradient)"
                        />
                        
                        {/* Red area fill (right side drop) */}
                        <path
                          d="M 400,100 
                             L 400,110 
                             L 390,105 
                             L 360,95 
                             L 330,85 
                             L 300,70 
                             L 270,55 
                             L 240,50 
                             L 210,45 
                             L 180,50 
                             L 150,60 
                             L 150,100 
                             Z"
                          fill="url(#chartAreaRedGradient)"
                        />
                        
                        {/* Volume bars at bottom */}
                        <rect x="5" y="170" width="15" height="25" fill="rgba(77,183,247,0.2)" />
                        <rect x="25" y="165" width="15" height="30" fill="rgba(77,183,247,0.25)" />
                        <rect x="45" y="160" width="15" height="35" fill="rgba(77,183,247,0.3)" />
                        <rect x="65" y="175" width="15" height="20" fill="rgba(77,183,247,0.2)" />
                        <rect x="85" y="155" width="15" height="40" fill="rgba(77,183,247,0.35)" />
                        <rect x="105" y="150" width="15" height="45" fill="rgba(77,183,247,0.4)" />
                        <rect x="125" y="145" width="15" height="50" fill="rgba(77,183,247,0.45)" />
                        <rect x="145" y="160" width="15" height="35" fill="rgba(16,185,129,0.3)" />
                        <rect x="165" y="155" width="15" height="40" fill="rgba(16,185,129,0.35)" />
                        <rect x="185" y="150" width="15" height="45" fill="rgba(16,185,129,0.4)" />
                        <rect x="205" y="145" width="15" height="50" fill="rgba(16,185,129,0.45)" />
                        <rect x="225" y="148" width="15" height="47" fill="rgba(16,185,129,0.4)" />
                        <rect x="245" y="152" width="15" height="43" fill="rgba(16,185,129,0.35)" />
                        <rect x="265" y="158" width="15" height="37" fill="rgba(16,185,129,0.3)" />
                        <rect x="285" y="162" width="15" height="33" fill="rgba(16,185,129,0.25)" />
                        <rect x="305" y="168" width="15" height="27" fill="rgba(16,185,129,0.2)" />
                        <rect x="325" y="172" width="15" height="23" fill="rgba(16,185,129,0.2)" />
                        <rect x="345" y="175" width="15" height="20" fill="rgba(248,113,113,0.2)" />
                        <rect x="365" y="178" width="15" height="17" fill="rgba(248,113,113,0.25)" />
                        <rect x="385" y="180" width="15" height="15" fill="rgba(248,113,113,0.3)" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              {module.content === "metrics" && (
                <div className="relative h-full w-full p-6">
                  {/* Soft-light gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5 mix-blend-soft-light" />
                  
                  {/* Metrics list */}
                  <div className="relative z-10 space-y-3">
                    {module.docCode && (
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-thema-faint opacity-50 mb-4">
                        {module.docCode}
                      </div>
                    )}
                    {heroMetrics.map((metric, idx) => (
                      <div key={idx} className="flex items-center justify-between font-mono text-[10px]">
                        <span className="text-thema-faint opacity-60 uppercase tracking-[0.1em]">
                          {metric.label}
                        </span>
                        <span
                          className={`font-semibold ${
                            metric.isPositive
                              ? "text-emerald-400 drop-shadow-[0_0_4px_rgba(16,185,129,0.4)]"
                              : "text-red-400 drop-shadow-[0_0_4px_rgba(248,113,113,0.4)]"
                          }`}
                        >
                          {metric.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {module.content === "tdd-label" && module.docCode && (
                <div className="relative h-full w-full p-4">
                  {/* Trading graph for top-most card */}
                  {module.id === "module-far-1" ? (
                    <>
                      <div className="mb-3 h-24 w-full rounded border border-thema-border-hairline bg-thema-surface/20 overflow-hidden">
                        <svg className="h-full w-full" viewBox="0 0 200 100" preserveAspectRatio="none">
                          {/* Grid lines */}
                          <defs>
                            <linearGradient id="tradingGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(77,183,247,0.4)" />
                              <stop offset="50%" stopColor="rgba(77,183,247,0.2)" />
                              <stop offset="100%" stopColor="rgba(77,183,247,0.1)" />
                            </linearGradient>
                          </defs>
                          {/* Background grid */}
                          <line x1="0" y1="25" x2="200" y2="25" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
                          <line x1="0" y1="50" x2="200" y2="50" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
                          <line x1="0" y1="75" x2="200" y2="75" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
                          {/* Trading line */}
                          <polyline
                            points="0,80 20,75 40,70 60,65 80,60 100,55 120,50 140,48 160,45 180,42 200,40"
                            fill="none"
                            stroke="url(#tradingGradient)"
                            strokeWidth="2"
                          />
                          {/* Area fill */}
                          <polygon
                            points="0,100 0,80 20,75 40,70 60,65 80,60 100,55 120,50 140,48 160,45 180,42 200,40 200,100"
                            fill="url(#tradingGradient)"
                            opacity="0.2"
                          />
                        </svg>
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-thema-faint opacity-50">
                        {module.docCode}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-thema-faint opacity-60">
                        {module.docCode}
                      </div>
                      {module.annotations && (
                        <div className="mt-2 space-y-1 font-mono text-[8px] uppercase tracking-[0.15em] text-thema-faint opacity-40">
                          {module.annotations.map((ann, i) => (
                            <div key={i}>{ann}</div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {module.content === "formula" && (
                <div className="relative p-4">
                  <div className="font-mono text-[9px] text-thema-faint opacity-50 leading-relaxed">
                    NAV(t) = Σ(price_i × qty_i)
                    <br />
                    Δ = ∂P/∂S
                    <br />
                    <span className="opacity-30">PnL = ∫(Δ × dS)</span>
                  </div>
                  {module.annotations && (
                    <div className="mt-3 font-mono text-[8px] uppercase tracking-[0.15em] text-thema-faint opacity-40">
                      {module.annotations[0]}
                    </div>
                  )}
                </div>
              )}

              {module.content === "nav-chart" && (
                <div className="relative p-4">
                  <div className="mb-2 h-20 w-full rounded border border-thema-border-hairline bg-thema-surface/20">
                    <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <polyline
                        points="0,80 20,70 40,60 60,50 80,45 100,40"
                        fill="none"
                        stroke="rgba(77,183,247,0.3)"
                        strokeWidth="1"
                      />
                    </svg>
                  </div>
                  <div className="font-mono text-[9px] text-thema-faint opacity-50">NAV TRACKING</div>
                  {module.annotations && (
                    <div className="mt-1 space-y-0.5 font-mono text-[8px] uppercase tracking-[0.15em] text-thema-faint opacity-40">
                      {module.annotations.map((ann, i) => (
                        <div key={i}>{ann}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {module.content === "nav-compound-chart" && (
                <div className="relative h-full w-full p-4">
                  {module.docCode && (
                    <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.15em] text-thema-faint opacity-50">
                      {module.docCode}
                    </div>
                  )}
                  <div className="h-[140px] w-full rounded border border-thema-border-hairline bg-thema-surface/20 overflow-hidden">
                    <svg className="h-full w-full" viewBox="0 0 200 140" preserveAspectRatio="none">
                      <defs>
                        {/* Grid pattern */}
                        <pattern id="navGrid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                          <line x1="0" y1="20" x2="20" y2="20" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
                          <line x1="20" y1="0" x2="20" y2="20" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
                        </pattern>
                      </defs>
                      
                      {/* Background grid */}
                      <rect width="200" height="140" fill="url(#navGrid)" />
                      
                      {/* NAV line (blue) - starts at same point, grows slower */}
                      <polyline
                        points="0,100 20,95 40,90 60,85 80,80 100,75 120,70 140,65 160,60 180,55 200,50"
                        fill="none"
                        stroke="rgba(77,183,247,0.5)"
                        strokeWidth="2"
                      />
                      
                      {/* Auto-compounded NAV line (green) - starts at same point, leads ahead */}
                      <polyline
                        points="0,100 20,92 40,84 60,76 80,68 100,60 120,52 140,44 160,36 180,28 200,20"
                        fill="none"
                        stroke="rgba(16,185,129,0.6)"
                        strokeWidth="2"
                      />
                      
                      {/* Area fill for NAV (blue, subtle) */}
                      <path
                        d="M 0,100 L 0,100 L 20,95 L 40,90 L 60,85 L 80,80 L 100,75 L 120,70 L 140,65 L 160,60 L 180,55 L 200,50 L 200,140 L 0,140 Z"
                        fill="rgba(77,183,247,0.08)"
                      />
                      
                      {/* Area fill for Auto-compounded NAV (green, subtle) */}
                      <path
                        d="M 0,100 L 0,100 L 20,92 L 40,84 L 60,76 L 80,68 L 100,60 L 120,52 L 140,44 L 160,36 L 180,28 L 200,20 L 200,140 L 0,140 Z"
                        fill="rgba(16,185,129,0.1)"
                      />
                    </svg>
                  </div>
                  {module.annotations && (
                    <div className="mt-2 space-y-1 font-mono text-[8px] uppercase tracking-[0.15em] text-thema-faint opacity-40">
                      {module.annotations.map((ann, i) => (
                        <div key={i}>{ann}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {module.content === "graph" && (
                <div className="relative p-4">
                  <div className="mb-2 h-16 w-full rounded border border-thema-border-hairline bg-thema-surface/20">
                    <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <path
                        d="M 0,50 Q 25,30 50,40 T 100,35"
                        fill="none"
                        stroke="rgba(61,231,208,0.3)"
                        strokeWidth="1"
                      />
                    </svg>
                  </div>
                  {module.annotations && (
                    <div className="space-y-0.5 font-mono text-[8px] uppercase tracking-[0.15em] text-thema-faint opacity-40">
                      {module.annotations.map((ann, i) => (
                        <div key={i}>{ann}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {module.content === "micro-grid" && (
                <div className="relative p-4">
                  <div className="h-full w-full bg-grid-faint bg-grid-sm opacity-[0.02]" />
                  {module.annotations && (
                    <div className="absolute bottom-4 left-4 font-mono text-[8px] uppercase tracking-[0.15em] text-thema-faint opacity-40">
                      {module.annotations[0]}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

